/**
 * `<ClientQRCode />` — Bug B fix (post-smoke): client-side SVG QR rendering.
 *
 * Renders a QR code synchronously as inline SVG using the `qrcode` package's
 * `QRCode.create()` matrix walk. Replaces the `<img src=/api/qr?...>` path
 * on `EventUrlsCards.tsx` to dodge the API Gateway `binaryMediaTypes` /
 * `Accept`-header negotiation bug. See
 * `_bmad-output/post-smoke-fixes/B-qr-codes-missing.md` §5 for context.
 *
 * Color choice mirrors the server-side defaults at
 * `api/lib/qr/index.ts:75-76` — white modules on `#0B0E13` background.
 *
 * `downloadQrPng` is a small companion helper that rasterizes the QR to a
 * canvas, converts it to a PNG blob, and triggers an anchor click for
 * download. Used by the "Download QR PNG" button on `EventUrlsCards.tsx`.
 */
import { useMemo } from 'react';
import QRCode from 'qrcode';

export interface ClientQRCodeProps {
  /** URL (or any string) to encode. */
  value: string;
  /** Rendered side length in CSS pixels. Default 128. */
  size?: number;
  /** Error correction level. Default 'Q' to match server defaults. */
  ec?: 'L' | 'M' | 'Q' | 'H';
  /** Accessible name (full sentence). */
  label: string;
  /** Optional `data-testid` on the wrapper div. */
  testId?: string;
}

const DARK_COLOR = '#FFFFFF';
const LIGHT_COLOR = '#0B0E13';
const QUIET_ZONE_MODULES = 4;

function buildQrSvg(value: string, ec: 'L' | 'M' | 'Q' | 'H', size: number): string {
  // qrcode throws "No input text" on empty strings. Render a blank
  // placeholder rectangle so callers can pass undefined/empty URLs
  // (e.g. Story 5.4 / 17.4 unit tests with stubbed responses).
  if (!value) {
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" ` +
      `width="${size}" height="${size}" ` +
      `viewBox="0 0 1 1" shape-rendering="crispEdges">` +
      `<rect width="1" height="1" fill="${LIGHT_COLOR}"/>` +
      `</svg>`
    );
  }
  const qr = QRCode.create(value, { errorCorrectionLevel: ec });
  const moduleCount = qr.modules.size;
  const data = qr.modules.data;
  const totalModules = moduleCount + QUIET_ZONE_MODULES * 2;
  const rects: string[] = [];
  for (let y = 0; y < moduleCount; y++) {
    for (let x = 0; x < moduleCount; x++) {
      if (data[y * moduleCount + x] === 1) {
        rects.push(
          `<rect x="${x + QUIET_ZONE_MODULES}" y="${y + QUIET_ZONE_MODULES}" width="1" height="1"/>`,
        );
      }
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" ` +
    `width="${size}" height="${size}" ` +
    `viewBox="0 0 ${totalModules} ${totalModules}" ` +
    `shape-rendering="crispEdges">` +
    `<rect width="${totalModules}" height="${totalModules}" fill="${LIGHT_COLOR}"/>` +
    `<g fill="${DARK_COLOR}">${rects.join('')}</g>` +
    `</svg>`
  );
}

export function ClientQRCode({
  value,
  size = 128,
  ec = 'Q',
  label,
  testId,
}: ClientQRCodeProps) {
  const svg = useMemo(() => buildQrSvg(value, ec, size), [value, ec, size]);
  return (
    <div
      role="img"
      aria-label={label}
      data-testid={testId}
      style={{ width: size, height: size, display: 'block' }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/**
 * Rasterize the QR to an 800x800 PNG and trigger a download. Uses
 * `QRCode.toCanvas` (async) + `canvas.toBlob` to keep the browser path
 * portable across Vite/jsdom without DOM-API surprises.
 */
export async function downloadQrPng(
  value: string,
  filename: string,
): Promise<void> {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, value, {
    width: 800,
    errorCorrectionLevel: 'Q',
    color: { dark: DARK_COLOR, light: LIGHT_COLOR },
  });
  await new Promise<void>((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve();
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      resolve();
    }, 'image/png');
  });
}
